import { useCallback, useEffect, useRef } from 'react';

import {
  getSideMenuPopoverAnchorElement,
  openSideMenuBadgePopover,
} from 'components/shared/side-menu/side-menu-badge-popover';
import {
  SIDE_MENU_POPOVER_GAP,
  SIDE_MENU_POPOVER_HOVER_INTENT_MS,
} from 'components/shared/side-menu/side-menu.constants';
import {
  canAutoOpenSideMenuPopover,
  recordSideMenuPopoverOpen,
  SIDE_MENU_POPOVER_CONFIG,
} from 'components/shared/side-menu/side-menu-popover-events';

import { useCurrentUser } from 'hooks/api/use-current-user';

import type { NavItemBadgePopover } from 'components/shared/side-menu/nav-config';

export interface UseSideMenuPopoverTriggerParams {
  popover?: NavItemBadgePopover;
  getPopoverAnchorElement?: () => HTMLElement | null;
  getPopoverFallbackAnchorElement?: () => HTMLElement | null;
  anchorGap?: number;
}

export interface UseSideMenuPopoverTriggerResult {
  trigger: 'badge' | 'item' | undefined;
  openPopoverNow: (sourceElement?: HTMLElement | null) => void;
  scheduleOpenPopover: (sourceElement?: HTMLElement | null) => void;
  cancelScheduledOpen: () => void;
}

export const useSideMenuPopoverTrigger = ({
  popover,
  getPopoverAnchorElement,
  getPopoverFallbackAnchorElement,
  anchorGap = SIDE_MENU_POPOVER_GAP.row,
}: UseSideMenuPopoverTriggerParams): UseSideMenuPopoverTriggerResult => {
  const { data: currentUser } = useCurrentUser();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const cancelScheduledOpen = useCallback((): void => {
    if (timeoutRef.current === undefined) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  }, []);

  useEffect(() => cancelScheduledOpen, [cancelScheduledOpen]);

  const openPopoverNow = useCallback(
    (sourceElement?: HTMLElement | null): void => {
      if (!popover) return;

      const isLimited = !currentUser?.internalUser;
      const oktaUserId = currentUser?.oktaUserId;

      if (isLimited && !canAutoOpenSideMenuPopover(popover, oktaUserId)) {
        return;
      }

      openSideMenuBadgePopover(popover, {
        anchorElement:
          getPopoverAnchorElement?.() ??
          (sourceElement
            ? getSideMenuPopoverAnchorElement(sourceElement)
            : null),
        fallbackAnchorElement: getPopoverFallbackAnchorElement?.(),
        anchorGap,
        onAutoOpened: isLimited
          ? () => recordSideMenuPopoverOpen(popover, oktaUserId)
          : undefined,
      });
    },
    [
      currentUser,
      popover,
      getPopoverAnchorElement,
      getPopoverFallbackAnchorElement,
      anchorGap,
    ]
  );

  const scheduleOpenPopover = useCallback(
    (sourceElement?: HTMLElement | null): void => {
      if (!popover || SIDE_MENU_POPOVER_CONFIG[popover].trigger !== 'item') {
        return;
      }

      cancelScheduledOpen();
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = undefined;
        openPopoverNow(sourceElement);
      }, SIDE_MENU_POPOVER_HOVER_INTENT_MS);
    },
    [popover, cancelScheduledOpen, openPopoverNow]
  );

  return {
    trigger: popover ? SIDE_MENU_POPOVER_CONFIG[popover].trigger : undefined,
    openPopoverNow,
    scheduleOpenPopover,
    cancelScheduledOpen,
  };
};
